import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Wallet, ShoppingBag, Users, Package, ArrowRight, TrendingUp, FileText, RefreshCw, DollarSign, Bell, Send } from 'lucide-react';
import { requestNotificationPermission, startNotificationPolling } from '@/utils/notifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MiniBarChart = ({ data, maxVal }) => {
  if (!data.length) return null;
  const max = maxVal || Math.max(...data.map(d => d.value), 1);
  return (
    <div className="flex items-end gap-1 h-20 mt-2" data-testid="spending-chart">
      {data.map((d, i) => (
        <div key={d.label ?? `bar-${i}`} className="flex-1 flex flex-col items-center gap-1">
          <div className="w-full bg-emerald-500 rounded-t-sm transition-all duration-300 min-h-[2px]"
            style={{ height: `${Math.max(2, (d.value / max) * 100)}%` }}
            title={`${d.label}: $${d.value.toFixed(2)}`} />
          <span className="text-[9px] text-slate-400">{d.label}</span>
        </div>
      ))}
    </div>
  );
};

const ClientDashboardPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [orders, setOrders] = useState([]);
  const [referrals, setReferrals] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rechargeRates, setRechargeRates] = useState([]);
  const [calcAmount, setCalcAmount] = useState('');
  const [calcCurrency, setCalcCurrency] = useState('USD');
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const h = { headers: getAuthHeaders() };
        const [w, o, r, f, rates, notifs] = await Promise.all([
          axios.get(`${API}/client/wallet/summary`, h),
          axios.get(`${API}/client/orders`, h),
          axios.get(`${API}/client/referrals`, h),
          axios.get(`${API}/client/favorites`, h).catch(() => ({ data: [] })),
          axios.get(`${API}/b2b/recharge-rates`, h).catch(() => ({ data: [] })),
          axios.get(`${API}/b2b/notifications`, h).catch(() => ({ data: [] })),
        ]);
        setWallet(w.data);
        setOrders(o.data.slice(0, 5));
        setReferrals(r.data);
        setFavorites(f.data);
        setRechargeRates(rates.data || []);
        setNotifications(notifs.data || []);
        // Request browser notification permission
        requestNotificationPermission();

        // Build monthly spending from orders
        const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        const now = new Date();
        const last6 = [];
        for (let i = 5; i >= 0; i--) {
          const m = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const key = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`;
          const spent = o.data.filter(od => od.created_at?.startsWith(key) && od.status !== 'cancelled').reduce((sum, od) => sum + (od.amount || 0), 0);
          last6.push({ label: months[m.getMonth()], value: spent });
        }
        setMonthlyData(last6);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  // Start notification polling
  useEffect(() => {
    const stop = startNotificationPolling(getAuthHeaders, API, 30000);
    return stop;
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  const statCards = [
    { label: 'Balance', value: `$${(wallet?.balance || 0).toFixed(2)}`, icon: Wallet, color: 'emerald', action: () => navigate('/cliente/wallet') },
    { label: 'Ordenes', value: orders.length, icon: Package, color: 'blue', action: () => navigate('/cliente/ordenes') },
    { label: 'Puntos', value: referrals?.total_points || 0, icon: TrendingUp, color: 'amber', action: () => navigate('/cliente/referidos') },
    { label: 'Referidos', value: referrals?.referrals?.length || 0, icon: Users, color: 'violet', action: () => navigate('/cliente/referidos') },
  ];
  const colorMap = { emerald: 'from-emerald-500 to-emerald-600', blue: 'from-blue-500 to-blue-600', amber: 'from-amber-500 to-amber-600', violet: 'from-violet-500 to-violet-600' };

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Hola, {user?.name}</h1>
            <p className="text-slate-500 dark:text-slate-400">Bienvenido a tu portal de servicios</p>
          </div>
          {/* Notifications Bell */}
          <div className="relative">
            <Button variant="ghost" size="sm" onClick={() => setShowNotifs(!showNotifs)} data-testid="notifs-bell">
              <Bell className="w-5 h-5" />
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                  {notifications.filter(n => !n.read).length}
                </span>
              )}
            </Button>
            {showNotifs && (
              <Card className="absolute right-0 top-10 w-72 max-h-80 overflow-y-auto z-50 shadow-xl" data-testid="notifs-dropdown">
                <div className="p-3 border-b"><h4 className="font-semibold text-sm text-slate-800 dark:text-white">Notificaciones</h4></div>
                {notifications.length === 0 ? (
                  <p className="p-4 text-xs text-slate-400 text-center">Sin notificaciones</p>
                ) : (
                  notifications.slice(0, 10).map(n => (
                    <div key={n.id} className={`p-3 border-b text-xs cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 ${!n.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`} data-testid={`notif-${n.id}`}>
                      <p className="font-medium text-slate-700 dark:text-slate-200">{n.title}</p>
                      <p className="text-slate-400 mt-0.5">{n.message}</p>
                      <p className="text-slate-300 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                  ))
                )}
              </Card>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s) => (
          <Card key={s.label} className="p-4 cursor-pointer hover:shadow-lg transition-shadow border-0" onClick={s.action} data-testid={`stat-${s.label.toLowerCase()}`}>
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colorMap[s.color]} flex items-center justify-center mb-3`}>
              <s.icon className="w-5 h-5 text-white" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{s.value}</p>
            <p className="text-sm text-slate-500">{s.label}</p>
          </Card>
        ))}
      </div>

      {/* Wallet Recharge Calculator */}
      <Card className="p-5 border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20" data-testid="recharge-calculator">
        <div className="flex items-center gap-2 mb-3">
          <RefreshCw className="w-5 h-5 text-emerald-600" />
          <h2 className="font-semibold text-slate-800 dark:text-white">Calculador de Recarga</h2>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Consulta cuanto cuesta recargar tu wallet en diferentes monedas</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Monto en USD</label>
            <Input type="number" step="0.01" min="1" placeholder="100.00" value={calcAmount} onChange={e => setCalcAmount(e.target.value)} className="bg-white dark:bg-slate-800" data-testid="calc-amount" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Pagar con</label>
            <select value={calcCurrency} onChange={e => setCalcCurrency(e.target.value)} className="w-full border rounded-md px-3 py-2 text-sm bg-white dark:bg-slate-800 dark:text-white" data-testid="calc-currency">
              <option value="USD">USD (Dolar)</option>
              <option value="UYU">UYU (Peso Uruguayo)</option>
              <option value="ZELLE">Zelle</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Recibes en wallet</label>
            <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 border rounded-md">
              <DollarSign className="w-4 h-4 text-emerald-600" />
              <span className="font-bold text-emerald-700 dark:text-emerald-400" data-testid="calc-result">
                {(() => {
                  if (!calcAmount || parseFloat(calcAmount) <= 0) return '$0.00';
                  const amt = parseFloat(calcAmount);
                  const rate = rechargeRates.find(r =>
                    r.currency?.toUpperCase() === calcCurrency ||
                    r.name?.toUpperCase()?.includes(calcCurrency)
                  );
                  if (calcCurrency === 'USD') return `$${amt.toFixed(2)}`;
                  if (rate) {
                    const rateVal = rate.rate_sell || rate.rate || 1;
                    return `$${(amt / rateVal).toFixed(2)}`;
                  }
                  return `$${amt.toFixed(2)}`;
                })()}
              </span>
            </div>
          </div>
        </div>

        {/* Rates preview */}
        {rechargeRates.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {rechargeRates.map(r => (
              <div key={r.id || r.currency} className="px-3 py-1.5 bg-white dark:bg-slate-800 rounded-lg text-xs border" data-testid={`rate-${r.currency || r.name}`}>
                <span className="font-medium text-slate-700 dark:text-slate-200">{r.name || r.currency}</span>
                <span className="text-slate-400 ml-1">{r.rate_sell || r.rate || '-'}</span>
              </div>
            ))}
          </div>
        )}

        <Button onClick={() => navigate('/cliente/wallet')} className="mt-4 w-full sm:w-auto" size="sm" data-testid="go-recharge-btn">
          <Wallet className="w-4 h-4 mr-2" /> Recargar Wallet Ahora
        </Button>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Spending Chart */}
        <Card className="p-6 border-0 shadow-sm">
          <h2 className="font-semibold text-slate-900 mb-1">Gastos mensuales</h2>
          <p className="text-xs text-slate-400 mb-2">Ultimos 6 meses</p>
          <MiniBarChart data={monthlyData} />
        </Card>

        {/* Quick Actions */}
        <Card className="p-6 border-0 shadow-sm">
          <h2 className="font-semibold text-slate-900 mb-4">Acciones rapidas</h2>
          <div className="grid grid-cols-2 gap-3">
            <Button onClick={() => navigate('/cliente/wallet')} className="h-auto py-4 flex-col gap-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200" variant="ghost" data-testid="quick-recargar">
              <Wallet className="w-6 h-6" />
              <span className="text-sm font-medium">Recargar Wallet</span>
            </Button>
            <Button onClick={() => navigate('/cliente/servicios')} className="h-auto py-4 flex-col gap-2 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200" variant="ghost" data-testid="quick-servicios">
              <ShoppingBag className="w-6 h-6" />
              <span className="text-sm font-medium">Ver Servicios</span>
            </Button>
            <Button onClick={() => navigate('/cliente/ordenes')} className="h-auto py-4 flex-col gap-2 bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200" variant="ghost" data-testid="quick-ordenes">
              <Package className="w-6 h-6" />
              <span className="text-sm font-medium">Mis Ordenes</span>
            </Button>
            <Button onClick={() => navigate('/cliente/referidos')} className="h-auto py-4 flex-col gap-2 bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200" variant="ghost" data-testid="quick-referidos">
              <Users className="w-6 h-6" />
              <span className="text-sm font-medium">Referidos</span>
            </Button>
            <Button onClick={() => navigate('/cliente/transferencias')} className="h-auto py-4 flex-col gap-2 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 border border-cyan-200 col-span-2" variant="ghost" data-testid="quick-envios">
              <Send className="w-6 h-6" />
              <span className="text-sm font-medium">Enviar Transferencia</span>
            </Button>
          </div>
        </Card>

        {/* Favorites */}
        {favorites.length > 0 && (
          <Card className="p-6 border-0 shadow-sm">
            <h2 className="font-semibold text-slate-900 mb-4">Servicios favoritos</h2>
            <div className="space-y-2">
              {favorites.map((f) => (
                <Button key={f.id} variant="outline" className="w-full justify-between h-auto py-3" onClick={() => navigate('/cliente/servicios')} data-testid={`fav-${f.id}`}>
                  <span className="text-sm">{f.name}</span>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                </Button>
              ))}
            </div>
          </Card>
        )}

        {/* Recent Orders */}
        <Card className="p-6 border-0 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-900">Ordenes recientes</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/cliente/ordenes')} className="text-emerald-600">
              Ver todas <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          {orders.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No tienes ordenes aun</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm text-slate-900">{o.service_type}</p>
                    <p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString('es-ES')}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-sm">${o.amount?.toFixed(2)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${o.status === 'completed' ? 'bg-green-100 text-green-700' : o.status === 'cancelled' ? 'bg-red-100 text-red-700' : o.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {o.status === 'completed' ? 'Completado' : o.status === 'cancelled' ? 'Cancelado' : o.status === 'in_progress' ? 'En Proceso' : 'Pendiente'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Legal Footer */}
      <div className="text-center pt-4 border-t">
        <div className="flex justify-center gap-4 text-xs text-slate-400">
          <Link to="/cliente/terminos" className="hover:text-slate-600" data-testid="link-terms">Terminos y Condiciones</Link>
          <span>|</span>
          <Link to="/cliente/terminos" className="hover:text-slate-600" data-testid="link-privacy">Politica de Privacidad</Link>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboardPage;
